import {
  ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis, Cell } from "recharts";

import ChartWrapper from "@/components/common/ChartWrapper";

import { useTheme } from "next-themes";
import { valueLabel, COLORS } from "@/lib/constants";
import { useGetAverageValuesQuery } from "@/hooks/queries";

// Function to map color names to actual color values
const getColorFromName = (colorName: string): string => {
  const colorMap: Record<string, string> = {
    // Basic colors
    red: "#DC2626",
    blue: "#2563EB",
    green: "#16A34A",
    yellow: "#CA8A04",
    orange: "#EA580C",
    purple: "#9333EA",
    pink: "#DB2777",
    brown: "#A16207",
    black: "#1F2937",
    white: "#F9FAFB",
    gray: "#6B7280",
    grey: "#6B7280",

    // Extended colors
    navy: "#1E40AF",
    maroon: "#991B1B",
    olive: "#65A30D",
    lime: "#65A30D",
    aqua: "#06B6D4",
    teal: "#0D9488",
    silver: "#9CA3AF",
    gold: "#F59E0B",
    coral: "#F97316",
    salmon: "#FB7185",
    khaki: "#FDE047",
    violet: "#8B5CF6",
    indigo: "#6366F1",
    cyan: "#06B6D4",
    magenta: "#EC4899",
    turquoise: "#14B8A6",
    beige: "#FEF3C7",
    ivory: "#FFFBEB",
    tan: "#D97706",
    crimson: "#DC2626",
    scarlet: "#EF4444",
    emerald: "#10B981",
    jade: "#059669",
    sapphire: "#3B82F6",
    ruby: "#E11D48",
    amber: "#F59E0B",
    bronze: "#92400E",
    rose: "#F43F5E",
    lavender: "#A78BFA",
    mint: "#6EE7B7",
    peach: "#FBBF24",
    plum: "#A855F7",
    sky: "#0EA5E9",
    slate: "#64748B",
    stone: "#78716C",
    neutral: "#737373",
    zinc: "#71717A",

    // Fallback for "Other" category
    other: "#6B7280",
  };

  // Convert to lowercase and try to match
  const normalizedName = colorName.toLowerCase().trim();

  // Direct match
  if (colorMap[normalizedName]) {
    return colorMap[normalizedName];
  }

  // Partial match for compound color names (e.g., "Dark Blue", "Light Green")
  const foundColor = Object.keys(colorMap).find(
    (key) => normalizedName.includes(key) || key.includes(normalizedName)
  );

  if (foundColor) {
    return colorMap[foundColor];
  }

  // Fallback to predefined colors array if no match found
  const fallbackIndex =
    Math.abs(
      normalizedName.split("").reduce((a, b) => {
        a = (a << 5) - a + b.charCodeAt(0);
        return a & a;
      }, 0)
    ) % COLORS.length;

  return COLORS[fallbackIndex];
};

const chartConfig: ChartConfig = {
  value: {
    label: "Average Rating",
    color: "#1A5BFA",
  },
};
export default function ColourChart({
  jobId,
  date,
  valueType,
}: {
  jobId: string;
  date: string;
  valueType: "mrp" | "asp" | "discount";
}) {
  const { data: averageRatingsData, isLoading: averageRatingsDataLoading } =
    useGetAverageValuesQuery({
      jobId: jobId,
      date: date,
    });

  const { theme } = useTheme();

  const chartData = averageRatingsData
    ? Object.entries(averageRatingsData.colour).map(([colour, value]) => ({
        colour,
        value: Number(value[valueType].toFixed(2)),
      }))
    : [];

  return (
    <ChartWrapper
      title="Colour"
      isLoading={averageRatingsDataLoading}
      className="flex-1"
      hasInfoIcon
      infoText={`The average ${valueLabel} of insights for a given colour.`}
    >
      <ChartContainer
        config={chartConfig}
        className="aspect-auto h-[250px] w-full"
      >
        <BarChart data={chartData}>
          <XAxis
            dataKey="colour"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            stroke={"#E0E0E0"}
            interval={"equidistantPreserveStart"}
          />
          <YAxis
            tickLine={true}
            axisLine={true}
            domain={[0, "dataMax"]}
            tickFormatter={(value) => value.toFixed(2)}
            interval={"preserveEnd"}
            stroke={"#E0E0E0"}
          />
          <CartesianGrid
            vertical={false}
            strokeDasharray={4}
            stroke={"#E0E0E0"}
            opacity={theme === "dark" ? 0.2 : 1}
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                className="w-[150px]"
                formatter={(value) => (
                  <div className="flex w-full items-center justify-between text-xs text-muted-foreground">
                    <span>{valueType}</span>
                    <span>{value}</span>
                  </div>
                )}
                hideIndicator
              />
            }
          />
          <Bar
            dataKey="value"
            fill={chartConfig.value.color}
            radius={[0, 0, 0, 0]}
            isAnimationActive={false}
          >
            {chartData?.map((entry, index) => (
              <Cell
                key={`cell-${index}`}
                fill={getColorFromName(entry.colour)}
              />
            ))}
          </Bar>
        </BarChart>
      </ChartContainer>
    </ChartWrapper>
  );
}
